import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Activity,
  BookOpen,
  Building2,
  CalendarCheck,
  Camera,
  CheckSquare,
  CircleHelp,
  ClipboardList,
  DoorOpen,
  Eye,
  FileQuestion,
  FileText,
  FolderOpen,
  Grid2X2,
  Home,
  Images,
  Layers,
  LayoutDashboard,
  LogOut,
  Plus,
  ShieldCheck,
  ShieldAlert,
  User,
  Users,
  Video,
  X,
} from 'lucide-react';
import { useUser } from '@/shared/hooks/useUser';
import { useCurrentClient } from '@/shared/hooks/useCurrentClient';
import {
  getVersionManifest,
  loadVersionManifest,
  type VersionManifest,
} from '@/utils/version-check';
import {
  useProjectsWithProgress,
  useProjectsWithAsbuilds,
  useProjectsWithDrones,
  useProjectsWithDronePhotos,
  useAllUserProjects,
} from '@/shared/hooks/useUserProjects';
import { Client } from '@/types/base';
import { logout } from '@/shared/api/authentication-api';
import { useToast } from '@/shared/hooks/use-toast';
import { FEATURE_FLAGS } from '@/features/featureFlags';
import { useFeatureAccess } from '@/shared/hooks/useFeatureAccess';
import { useSandboxMode } from '@/features/sandbox';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { Button } from '@/shared/ui/button';
import {
  IRONVIEW_LOGO_SRC,
  applyIronviewLogoFallback,
} from '@/shared/components/IronviewLogo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/ui/accordion';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavLinkProps {
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
}

type SidebarProject = {
  id: number;
  name: string;
  is_active?: boolean;
  is_sandbox?: boolean;
  progress_directory?: string | null;
  asbuilds_directory?: string | null;
  drones_directory?: string | null;
  drone_photos_link?: string | null;
  /** RBAC permission codenames from the dashboard payload. */
  permissions?: string[];
};

type ActiveState = {
  section: string | null;
  projectId: number | null;
  projectName: string | null;
};

type ProjectModule = {
  value: string;
  label: string;
  icon: React.ReactNode;
  projects: SidebarProject[];
  isLoading: boolean;
  emptyLabel: string;
  isEnabled?: boolean;
  getHref?: (project: SidebarProject) => string;
  onProjectClick?: (project: SidebarProject) => void;
  isProjectActive?: (project: SidebarProject) => boolean;
};

type FeatureNavItem = {
  value: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  isEnabled: boolean;
  external?: boolean;
};

const navItemClass =
  'group relative flex w-full items-center justify-start gap-3 rounded-[10px] px-3 py-2.5 text-sm font-semibold transition-colors duration-150';
const navIdleClass = 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground [&_svg]:text-muted-foreground [&_svg]:transition-colors [&_svg]:duration-150 hover:[&_svg]:text-foreground';
const navActiveClass = 'bg-primary/12 text-foreground before:absolute before:-left-3.5 before:inset-y-2 before:w-[3px] before:rounded before:bg-primary [&_svg]:text-primary';

const NavLink: React.FC<NavLinkProps> = ({
  href,
  icon,
  children,
  isActive = false,
  onClick,
}) => (
  <Button
    asChild
    variant="ghost"
    className={`${navItemClass} ${isActive ? navActiveClass : navIdleClass}`}
  >
    <Link to={href} onClick={onClick}>
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>
      <span className="min-w-0 truncate text-left">{children}</span>
    </Link>
  </Button>
);

type FeatureNavButtonProps = FeatureNavItem & {
  activeSection: string | null;
  onNavigate?: () => void;
};

const FeatureNavButton: React.FC<FeatureNavButtonProps> = ({
  value,
  label,
  icon,
  href,
  isEnabled = true,
  external = false,
  activeSection,
  onNavigate,
}) => {
  const isActive = activeSection === value;
  const className = `${navItemClass} ${isActive ? navActiveClass : navIdleClass}`;

  if (!isEnabled || !href) {
    return (
      <Button
        variant="ghost"
        disabled
        aria-disabled
        className={`${className} cursor-not-allowed opacity-45`}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>
        <span className="min-w-0 truncate text-left">{label}</span>
      </Button>
    );
  }

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>
        <span className="min-w-0 truncate text-left">{label}</span>
      </a>
    );
  }

  return (
    <NavLink href={href} icon={icon} isActive={isActive} onClick={onNavigate}>
      {label}
    </NavLink>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-2 ml-3 mt-[18px] text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
    {children}
  </div>
);

const VersionFooter: React.FC = () => {
  const [info, setInfo] = useState<VersionManifest | null>(getVersionManifest);

  useEffect(() => {
    if (info !== null) return;
    let cancelled = false;
    void loadVersionManifest().then((manifest) => {
      if (!cancelled) setInfo(manifest);
    });
    return () => {
      cancelled = true;
    };
  }, [info]);

  if (info === null) return null;

  return (
    <div className="px-3 pt-2 text-[10px] leading-tight text-muted-foreground/70 select-text">
      <div className="font-mono truncate">v{info.version}</div>
    </div>
  );
};

const getClientLogo = (client: Client | null) => {
  if (!client) {
    return { type: 'fallback', url: IRONVIEW_LOGO_SRC };
  }

  if (client.logo_svg_url) return { type: 'svg', url: client.logo_svg_url };
  if (client.logo_light_url && client.logo_dark_url) {
    return { type: 'dual', lightUrl: client.logo_light_url, darkUrl: client.logo_dark_url };
  }
  if (client.logo_light_url) return { type: 'single', url: client.logo_light_url };
  if (client.logo_svg) return { type: 'svg', url: client.logo_svg };
  if (client.logo_light && client.logo_dark) {
    return { type: 'dual', lightUrl: client.logo_light, darkUrl: client.logo_dark };
  }
  if (client.logo_light) return { type: 'single', url: client.logo_light };

  return { type: 'fallback', url: IRONVIEW_LOGO_SRC };
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useUser();
  const { currentClient } = useCurrentClient();
  const { toast } = useToast();
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const drawingsAccess = useFeatureAccess(FEATURE_FLAGS.DRAWINGS);
  const punchListsAccess = useFeatureAccess(FEATURE_FLAGS.PUNCH_LISTS);
  const observationsAccess = useFeatureAccess(FEATURE_FLAGS.OBSERVATIONS);
  const safetyAccess = useFeatureAccess(FEATURE_FLAGS.SAFETY);
  const photoGalleryAccess = useFeatureAccess(FEATURE_FLAGS.PHOTO_GALLERY);
  const rfisAccess = useFeatureAccess(FEATURE_FLAGS.RFIS);
  const submittalsAccess = useFeatureAccess(FEATURE_FLAGS.SUBMITTALS);
  const filesAccess = useFeatureAccess(FEATURE_FLAGS.FILES);
  const dailyLogAccess = useFeatureAccess(FEATURE_FLAGS.DAILY_LOG);
  const meetingMinutesAccess = useFeatureAccess(FEATURE_FLAGS.MEETING_MINUTES);
  const trainingAccess = useFeatureAccess(FEATURE_FLAGS.TRAINING);
  const supportAccess = useFeatureAccess(FEATURE_FLAGS.SUPPORT);
  const { inSandboxMode } = useSandboxMode();
  const {
    projectIdNumber,
    routeProjectIdNumber,
    routeProjectName,
    selectedProjectIdNumber,
    clearSelectedProject,
  } = useCurrentProject();

  const resolvedClientName = currentClient?.name ?? user?.client_info?.name;
  const clientName = resolvedClientName ?? 'dashboard';
  const canSeeClientAdmin = Boolean(user?.is_staff || user?.is_superuser || user?.is_client_admin);

  // While auth/client context is loading, use the current-user endpoint. The
  // "dashboard" fallback is only a temporary link target, not a client name.
  const progressData = useProjectsWithProgress(resolvedClientName);
  const asbuildsData = useProjectsWithAsbuilds(resolvedClientName);
  const dronesData = useProjectsWithDrones(resolvedClientName);
  const dronePhotosData = useProjectsWithDronePhotos(resolvedClientName);
  const allProjectsData = useAllUserProjects(resolvedClientName);

  useEffect(() => {
    const path = location.pathname;
    const propertyWithClientMatch = path.match(/\/app\/property\/[^/]+\/\d+\/(dashboard|progress|asbuilds|drones|drawings|punch-lists|observations|safety|rfis|submittals|photos|files|daily-log|meeting-minutes)/);
    const propertyLegacyMatch = path.match(/\/app\/property\/\d+\/(dashboard|progress|asbuilds|drones|drawings|punch-lists|observations|safety|rfis|submittals|photos|files|daily-log|meeting-minutes)/);
    const teamMatch = path.match(/\/app\/team\//);
    const dronePhotosMatch = path.match(/\/app\/property\/[^/]*\/\d+\/drone-photos/);

    let section: string | null = null;
    if (propertyWithClientMatch) section = propertyWithClientMatch[1] === 'dashboard' ? 'project-dashboard' : propertyWithClientMatch[1];
    else if (propertyLegacyMatch) section = propertyLegacyMatch[1] === 'dashboard' ? 'project-dashboard' : propertyLegacyMatch[1];
    else if (dronePhotosMatch) section = 'drone-photos';
    else if (teamMatch) section = 'team';

    if (section) {
      setOpenAccordions((prev) => (prev.includes(section!) ? prev : [...prev, section!]));
    }
  }, [location.pathname]);

  const activeState: ActiveState = useMemo(() => {
    const path = location.pathname;
    const propertyWithClientMatch = path.match(/\/app\/property\/[^/]+\/(\d+)\/(dashboard|progress|asbuilds|drones|drawings|punch-lists|observations|safety|rfis|submittals|photos|files|daily-log|meeting-minutes)/);
    if (propertyWithClientMatch) {
      return {
        section: propertyWithClientMatch[2] === 'dashboard' ? 'project-dashboard' : propertyWithClientMatch[2],
        projectId: Number(propertyWithClientMatch[1]),
        projectName: null,
      };
    }

    const propertyMatch = path.match(/\/app\/property\/(\d+)\/(dashboard|progress|asbuilds|drones|drawings|punch-lists|observations|safety|rfis|submittals|photos|files|daily-log|meeting-minutes)/);
    if (propertyMatch) {
      return {
        section: propertyMatch[2] === 'dashboard' ? 'project-dashboard' : propertyMatch[2],
        projectId: Number(propertyMatch[1]),
        projectName: null,
      };
    }

    const teamMatch = path.match(/\/app\/team\/([^/]+)\/([^/]+)/);
    if (teamMatch) {
      return { section: 'team', projectId: null, projectName: decodeURIComponent(teamMatch[2]) };
    }

    return { section: null, projectId: null, projectName: null };
  }, [location.pathname]);

  const sandboxFilter = (project: SidebarProject) =>
    inSandboxMode ? Boolean(project.is_sandbox) : !project.is_sandbox;
  const activeProjectFilter = (project: SidebarProject) => Boolean(project.is_active);
  const prepareProjects = (projects: SidebarProject[]) =>
    projects.filter(activeProjectFilter).filter(sandboxFilter);
  const prepareSelectableProjects = (projects: SidebarProject[]) => projects.filter(sandboxFilter);

  const selectableProjects = prepareSelectableProjects(allProjectsData.allProjects);
  const progressProjects = prepareProjects(progressData.progressProjects);
  const asbuildsProjects = prepareProjects(asbuildsData.asbuildsProjects);
  const dronesProjects = prepareProjects(dronesData.dronesProjects);
  const dronePhotosProjects = prepareProjects(dronePhotosData.dronePhotosProjects);
  const allProjects = prepareProjects(allProjectsData.allProjects);
  const contextProjectIdNumber = routeProjectIdNumber ?? activeState.projectId ?? selectedProjectIdNumber ?? projectIdNumber;
  const selectedProject = useMemo<SidebarProject | undefined>(() => {
    if (contextProjectIdNumber != null) {
      const projectFromLists = selectableProjects.find((project) => project.id === contextProjectIdNumber);
      const projectFromCatalog = allProjectsData.allProjects.find(
        (project) => project.id === contextProjectIdNumber,
      );
      return (
        projectFromLists ??
        projectFromCatalog ?? {
          id: contextProjectIdNumber,
          name: routeProjectName ?? `Project ${contextProjectIdNumber}`,
        }
      );
    }
    if (routeProjectName) {
      return selectableProjects.find(
        (project) => decodeURIComponent(project.name) === decodeURIComponent(routeProjectName),
      );
    }
    return undefined;
  }, [contextProjectIdNumber, routeProjectName, selectableProjects, allProjectsData.allProjects]);

  const projectHref = (project: SidebarProject, section: string) =>
    `/app/property/${clientName}/${project.id}/${section}`;

  const projectIsActive = (project: SidebarProject, section: string) =>
    activeState.section === section && activeState.projectId === project.id;

  const nameIsActive = (project: SidebarProject, section: string) =>
    activeState.section === section &&
    activeState.projectName !== null &&
    decodeURIComponent(activeState.projectName) === decodeURIComponent(project.name);

  const hasProjectValue = (value?: string | null) =>
    Boolean(value && value.trim() !== '' && value.trim().toLowerCase() !== 'none');

  // Per-project RBAC gating, mirroring the main dashboard's project card
  // (pages/Dashboard.tsx). Projects from the dashboard payload carry their
  // permission codenames; staff/superusers bypass so deep links to projects
  // outside their lists (which lack the payload) aren't grayed out.
  const unrestricted = Boolean(user?.is_staff || user?.is_superuser);
  const projectAllows = (project: SidebarProject, codename: string) =>
    unrestricted || (project.permissions ?? []).includes(codename);
  const withPermission = (projects: SidebarProject[], codename: string) =>
    projects.filter((project) => projectAllows(project, codename));

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      localStorage.removeItem('token');
      toast({
        title: 'Success',
        description: 'Logged out successfully',
        variant: 'default',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: 'Error',
        description: 'Failed to logout. Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
      setIsLoggingOut(false);
    }
  };

  const logoInfo = getClientLogo(currentClient);

  const renderLogo = () => (
    <div className="flex items-center justify-center px-3 pb-4 pt-3">
      {logoInfo.type === 'dual' ? (
        <>
          <img
            className="max-h-20 w-auto max-w-[11rem] object-contain dark:hidden"
            src={logoInfo.lightUrl}
            alt={currentClient?.name || 'Client Logo'}
            onError={(e) => {
              applyIronviewLogoFallback(e.currentTarget, 'dark');
            }}
          />
          <img
            className="hidden max-h-20 w-auto max-w-[11rem] object-contain dark:block"
            src={logoInfo.darkUrl}
            alt={currentClient?.name || 'Client Logo'}
            onError={(e) => {
              applyIronviewLogoFallback(e.currentTarget, 'light');
            }}
          />
        </>
      ) : (
        <img
          className={`max-h-20 w-auto max-w-[11rem] object-contain ${
            logoInfo.type === 'fallback' ? 'dark:brightness-0 dark:invert' : ''
          }`}
          src={logoInfo.url || logoInfo.lightUrl || IRONVIEW_LOGO_SRC}
          alt={currentClient?.name || 'Client Logo'}
          onError={(e) => {
            applyIronviewLogoFallback(e.currentTarget);
          }}
        />
      )}
    </div>
  );

  const ProjectAccordion: React.FC<ProjectModule> = ({
    value,
    label,
    icon,
    projects,
    isLoading,
    emptyLabel,
    isEnabled = true,
    getHref,
    onProjectClick,
    isProjectActive,
  }) => {
    const active = activeState.section === value;

    if (!isEnabled) {
      return (
        <Button
          variant="ghost"
          disabled
          className={`${navItemClass} cursor-not-allowed text-muted-foreground/50 opacity-60`}
        >
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>
          <span className="min-w-0 truncate text-left">{label}</span>
        </Button>
      );
    }

    return (
      <AccordionItem value={value} className="border-none">
        <AccordionTrigger
          className={`${navItemClass} py-2.5 hover:no-underline ${
            active ? navActiveClass : navIdleClass
          }`}
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center [&_svg]:h-5 [&_svg]:w-5">{icon}</span>
            <span className="min-w-0 truncate">{label}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent className="pb-1 pt-1">
          {isLoading ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">Loading...</div>
          ) : projects.length > 0 ? (
            <div className="ml-5 border-l border-sidebar-border/70 pl-2">
              {projects.map((project) => {
                const projectActive = isProjectActive?.(project) ?? projectIsActive(project, value);
                return (
                  <Button
                    key={project.id}
                    variant="ghost"
                    className={`mb-0.5 h-8 w-full justify-start rounded-[9px] px-2 text-left text-xs font-medium ${
                      projectActive
                        ? 'bg-primary/12 text-foreground'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground'
                    }`}
                    onClick={() => {
                      if (onProjectClick) {
                        onProjectClick(project);
                        return;
                      }
                      if (getHref) navigate(getHref(project));
                    }}
                  >
                    <span className="truncate">{project.name}</span>
                  </Button>
                );
              })}
            </div>
          ) : (
            <div className="px-3 py-2 text-xs text-muted-foreground">{emptyLabel}</div>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  const generalProjectModules: ProjectModule[] = [
    {
      value: 'project-dashboard',
      label: 'Project Dashboard',
      icon: <LayoutDashboard className="h-4 w-4" />,
      projects: withPermission(allProjects, 'view_project_summary'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      getHref: (project) => projectHref(project, 'dashboard'),
    },
  ];

  const siteDocumentationModules: ProjectModule[] = [
    {
      value: 'progress',
      label: 'In Progress',
      icon: <Activity className="h-4 w-4" />,
      projects: withPermission(progressProjects, 'can_view_progress'),
      isLoading: progressData.isLoading,
      emptyLabel: 'No active projects',
      getHref: (project) => projectHref(project, 'progress'),
    },
    {
      value: 'asbuilds',
      label: 'As Build',
      icon: <BookOpen className="h-4 w-4" />,
      projects: withPermission(asbuildsProjects, 'can_view_asbuilds'),
      isLoading: asbuildsData.isLoading,
      emptyLabel: 'No completed projects',
      getHref: (project) => projectHref(project, 'asbuilds'),
    },
    {
      value: 'drones',
      label: 'Post-Tension Decks',
      icon: <Layers className="h-4 w-4" />,
      projects: withPermission(dronesProjects, 'can_view_drone_videos'),
      isLoading: dronesData.isLoading,
      emptyLabel: 'No post-tension projects',
      getHref: (project) => projectHref(project, 'drones'),
    },
    {
      value: 'drone-photos',
      label: 'Drone Photos',
      icon: <Camera className="h-4 w-4" />,
      projects: withPermission(dronePhotosProjects, 'can_view_drone_photos'),
      isLoading: dronePhotosData.isLoading,
      emptyLabel: 'No drone photo projects',
      onProjectClick: (project) => {
        if (project.drone_photos_link) {
          window.open(project.drone_photos_link, '_blank', 'noopener,noreferrer');
        }
      },
    },
    {
      value: 'photos',
      label: 'Photo Gallery',
      icon: <Images className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_photo_gallery'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: photoGalleryAccess.isEnabled,
      getHref: (project) => projectHref(project, 'photos'),
    },
  ];

  const managementModules: ProjectModule[] = [
    {
      value: 'drawings',
      label: 'Drawings',
      icon: <FileText className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_drawings'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: drawingsAccess.isEnabled,
      getHref: (project) => projectHref(project, 'drawings'),
    },
    {
      value: 'punch-lists',
      label: 'Punch Lists',
      icon: <CheckSquare className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_punch_lists'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: punchListsAccess.isEnabled,
      getHref: (project) => projectHref(project, 'punch-lists'),
    },
    {
      value: 'observations',
      label: 'Observations',
      icon: <Eye className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_observations'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: observationsAccess.isEnabled,
      getHref: (project) => projectHref(project, 'observations'),
    },
    {
      value: 'safety',
      label: 'Safety',
      icon: <ShieldAlert className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_safety'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: safetyAccess.isEnabled,
      getHref: (project) => projectHref(project, 'safety'),
    },
    {
      value: 'rfis',
      label: 'RFIs',
      icon: <FileQuestion className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_rfis'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: rfisAccess.isEnabled,
      getHref: (project) => projectHref(project, 'rfis'),
    },
    {
      value: 'submittals',
      label: 'Submittals',
      icon: <ClipboardList className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_submittals'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: submittalsAccess.isEnabled,
      getHref: (project) => projectHref(project, 'submittals'),
    },
    {
      value: 'files',
      label: 'Files',
      icon: <FolderOpen className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_files'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: filesAccess.isEnabled,
      getHref: (project) => projectHref(project, 'files'),
    },
    {
      value: 'daily-log',
      label: 'Daily Log',
      icon: <CalendarCheck className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_daily_logs'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: dailyLogAccess.isEnabled,
      getHref: (project) => projectHref(project, 'daily-log'),
    },
  ];

  const personnelModules: ProjectModule[] = [
    {
      value: 'team',
      label: 'Team',
      icon: <Users className="h-4 w-4" />,
      projects: withPermission(allProjects, 'view_project_memberships'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      getHref: (project) => `/app/team/${clientName}/${project.name}`,
      isProjectActive: (project) => nameIsActive(project, 'team'),
    },
    {
      value: 'meeting-minutes',
      label: 'Meetings',
      icon: <Video className="h-4 w-4" />,
      projects: withPermission(allProjects, 'can_view_meetings'),
      isLoading: allProjectsData.isLoading,
      emptyLabel: 'No projects',
      isEnabled: meetingMinutesAccess.isEnabled,
      getHref: (project) => projectHref(project, 'meeting-minutes'),
    },
  ];

  const selectedProjectFeatureGroups = selectedProject
    ? [
        {
          label: selectedProject.name,
          items: [
            {
              value: 'project-dashboard',
              label: 'Project Dashboard',
              icon: <LayoutDashboard className="h-4 w-4" />,
              href: projectHref(selectedProject, 'dashboard'),
              isEnabled: projectAllows(selectedProject, 'view_project_summary'),
            },
          ],
        },
        {
          label: 'Ironview 360',
          items: [
            {
              value: 'progress',
              label: 'In Progress',
              icon: <Activity className="h-4 w-4" />,
              href: projectHref(selectedProject, 'progress'),
              isEnabled:
                hasProjectValue(selectedProject.progress_directory) &&
                projectAllows(selectedProject, 'can_view_progress'),
            },
            {
              value: 'asbuilds',
              label: 'As Build',
              icon: <BookOpen className="h-4 w-4" />,
              href: projectHref(selectedProject, 'asbuilds'),
              isEnabled:
                hasProjectValue(selectedProject.asbuilds_directory) &&
                projectAllows(selectedProject, 'can_view_asbuilds'),
            },
            {
              value: 'drones',
              label: 'Post-Tension Decks',
              icon: <Layers className="h-4 w-4" />,
              href: projectHref(selectedProject, 'drones'),
              isEnabled:
                hasProjectValue(selectedProject.drones_directory) &&
                projectAllows(selectedProject, 'can_view_drone_videos'),
            },
            {
              value: 'drone-photos',
              label: 'Drone Photos',
              icon: <Camera className="h-4 w-4" />,
              href: selectedProject.drone_photos_link || undefined,
              external: true,
              isEnabled:
                hasProjectValue(selectedProject.drone_photos_link) &&
                projectAllows(selectedProject, 'can_view_drone_photos'),
            },
            {
              value: 'photos',
              label: 'Photo Gallery',
              icon: <Images className="h-4 w-4" />,
              href: projectHref(selectedProject, 'photos'),
              isEnabled:
                photoGalleryAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_photo_gallery'),
            },
          ],
        },
        {
          label: 'Construction Management',
          items: [
            {
              value: 'drawings',
              label: 'Drawings',
              icon: <FileText className="h-4 w-4" />,
              href: projectHref(selectedProject, 'drawings'),
              isEnabled:
                drawingsAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_drawings'),
            },
            {
              value: 'punch-lists',
              label: 'Punch Lists',
              icon: <CheckSquare className="h-4 w-4" />,
              href: projectHref(selectedProject, 'punch-lists'),
              isEnabled:
                punchListsAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_punch_lists'),
            },
            {
              value: 'observations',
              label: 'Observations',
              icon: <Eye className="h-4 w-4" />,
              href: projectHref(selectedProject, 'observations'),
              isEnabled:
                observationsAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_observations'),
            },
            {
              value: 'safety',
              label: 'Safety',
              icon: <ShieldAlert className="h-4 w-4" />,
              href: projectHref(selectedProject, 'safety'),
              isEnabled:
                safetyAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_safety'),
            },
            {
              value: 'rfis',
              label: 'RFIs',
              icon: <FileQuestion className="h-4 w-4" />,
              href: projectHref(selectedProject, 'rfis'),
              isEnabled:
                rfisAccess.isEnabled && projectAllows(selectedProject, 'can_view_rfis'),
            },
            {
              value: 'submittals',
              label: 'Submittals',
              icon: <ClipboardList className="h-4 w-4" />,
              href: projectHref(selectedProject, 'submittals'),
              isEnabled:
                submittalsAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_submittals'),
            },
            {
              value: 'files',
              label: 'Files',
              icon: <FolderOpen className="h-4 w-4" />,
              href: projectHref(selectedProject, 'files'),
              isEnabled:
                filesAccess.isEnabled && projectAllows(selectedProject, 'can_view_files'),
            },
            {
              value: 'daily-log',
              label: 'Daily Log',
              icon: <CalendarCheck className="h-4 w-4" />,
              href: projectHref(selectedProject, 'daily-log'),
              isEnabled:
                dailyLogAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_daily_logs'),
            },
          ],
        },
        {
          label: 'Personnel Management',
          items: [
            {
              value: 'team',
              label: 'Team',
              icon: <Users className="h-4 w-4" />,
              href: `/app/team/${encodeURIComponent(clientName)}/${encodeURIComponent(selectedProject.name)}`,
              isEnabled: projectAllows(selectedProject, 'view_project_memberships'),
            },
            {
              value: 'meeting-minutes',
              label: 'Meetings',
              icon: <Video className="h-4 w-4" />,
              href: projectHref(selectedProject, 'meeting-minutes'),
              isEnabled:
                meetingMinutesAccess.isEnabled &&
                projectAllows(selectedProject, 'can_view_meetings'),
            },
          ],
        },
      ]
    : [];

  const sidebarContent = (
    <div className="slate-scroll pointer-events-auto flex grow flex-col overflow-y-auto border-r border-sidebar-border bg-sidebar px-3.5 pb-6 pt-3">
      {renderLogo()}

      <nav className="flex flex-1 flex-col">
        <div className="space-y-0.5">
          <SectionLabel>General</SectionLabel>
          <NavLink
            href={`/app/dashboard/${clientName}`}
            icon={<Home className="h-4 w-4" />}
            isActive={location.pathname.includes('/app/dashboard')}
            onClick={clearSelectedProject}
          >
            All Projects
          </NavLink>
          <NavLink
            href="/app/schedule-visit"
            icon={<DoorOpen className="h-4 w-4" />}
            isActive={location.pathname === '/app/schedule-visit'}
          >
            Schedule a Visit
          </NavLink>

          {selectedProject ? (
            <>
              {selectedProjectFeatureGroups.map((group) => (
                <div key={group.label}>
                  <SectionLabel>{group.label}</SectionLabel>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <FeatureNavButton
                        key={item.value}
                        {...item}
                        activeSection={activeState.section}
                        onNavigate={onClose}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <Accordion
              type="multiple"
              value={openAccordions}
              onValueChange={setOpenAccordions}
              className="space-y-0.5"
            >
              {generalProjectModules.map((module) => (
                <ProjectAccordion key={module.value} {...module} />
              ))}

              <SectionLabel>Ironview 360</SectionLabel>
              {siteDocumentationModules.map((module) => (
                <ProjectAccordion key={module.value} {...module} />
              ))}

              <SectionLabel>Construction Management</SectionLabel>
              {managementModules.map((module) => (
                <ProjectAccordion key={module.value} {...module} />
              ))}

              <SectionLabel>Personnel Management</SectionLabel>
              {personnelModules.map((module) => (
                <ProjectAccordion key={module.value} {...module} />
              ))}
            </Accordion>
          )}

          {trainingAccess.isEnabled ? (
            <NavLink
              href={`/app/training/${clientName}`}
              icon={<BookOpen className="h-4 w-4" />}
              isActive={location.pathname.includes('/training')}
            >
              Training
            </NavLink>
          ) : (
            <Button
              variant="ghost"
              disabled
              className={`${navItemClass} cursor-not-allowed text-muted-foreground/50 opacity-60`}
            >
              <BookOpen className="h-4 w-4" />
              <span className="min-w-0 truncate text-left">Training</span>
            </Button>
          )}

          {canSeeClientAdmin && (
            <>
              <SectionLabel>Organization Management</SectionLabel>
              <NavLink
                href={`/app/admin/${clientName}`}
                icon={<ShieldCheck className="h-4 w-4" />}
                isActive={location.pathname.startsWith('/app/admin/')}
              >
                Administration
              </NavLink>
            </>
          )}

          {user?.is_staff && (
            <>
              <SectionLabel>Staff Only</SectionLabel>
              <Accordion
                type="multiple"
                value={openAccordions}
                onValueChange={setOpenAccordions}
                className="space-y-0.5"
              >
                <AccordionItem value="add-new" className="border-none">
                  <AccordionTrigger className={`${navItemClass} py-2.5 text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground hover:no-underline`}>
                    <span className="flex items-center gap-2.5">
                      <Plus className="h-4 w-4" />
                      <span>Add New</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-1 pt-1">
                    <div className="ml-5 border-l border-sidebar-border/70 pl-2">
                      {[
                        ['Add Scan', '/app/scan/add'],
                        ['Add Layout', '/app/layout/add'],
                        ['Upload Drone Video', '/app/drone/upload'],
                        ['Smart Valves', '/app/smart-valves'],
                        ['Capture Lead', '/app/leads/capture'],
                      ].map(([label, href]) => (
                        <Button
                          key={href}
                          variant="ghost"
                          className={`mb-0.5 h-8 w-full justify-start rounded-[9px] px-2 text-left text-xs font-medium ${
                            location.pathname === href
                              ? 'bg-primary/12 text-foreground'
                              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground'
                          }`}
                          onClick={() => navigate(href)}
                        >
                          <span className="truncate">{label}</span>
                        </Button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <NavLink
                href="/app/clients/all"
                icon={<Building2 className="h-4 w-4" />}
                isActive={location.pathname === '/app/clients/all'}
              >
                All Clients
              </NavLink>
              <NavLink
                href="/app/projects/all"
                icon={<Grid2X2 className="h-4 w-4" />}
                isActive={location.pathname === '/app/projects/all'}
              >
                All Projects
              </NavLink>
              <a
                href="/admin/"
                className={`${navItemClass} ${navIdleClass}`}
              >
                <ShieldCheck className="h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate text-left">Site Admin</span>
              </a>
            </>
          )}
        </div>

        <div className="mt-auto space-y-0.5 border-t border-sidebar-border/70 pt-3">
          <SectionLabel>Account</SectionLabel>
          <NavLink
            href="/app/account"
            icon={<User className="h-4 w-4" />}
            isActive={location.pathname === '/app/account'}
          >
            My Account
          </NavLink>
          {supportAccess.isEnabled && (
            <NavLink
              href="/app/support"
              icon={<CircleHelp className="h-4 w-4" />}
              isActive={location.pathname === '/app/support'}
            >
              Support
            </NavLink>
          )}

          <Button
            variant="ghost"
            className={`${navItemClass} text-sidebar-foreground hover:bg-destructive/10 hover:text-destructive`}
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className="min-w-0 truncate text-left">
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </span>
          </Button>

          <VersionFooter />
        </div>
      </nav>
    </div>
  );

  return (
    <>
      <div className="pointer-events-auto hidden lg:fixed lg:inset-y-0 lg:z-[1200] lg:flex lg:w-[17rem] lg:flex-col">
        {sidebarContent}
      </div>

      <div className={`pointer-events-auto relative z-[1200] lg:hidden ${isOpen ? '' : 'hidden'}`}>
        <div className="pointer-events-auto fixed inset-0 flex">
          <div className="relative mr-16 flex w-full max-w-sm flex-1">
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
              <Button
                variant="ghost"
                size="icon"
                className="-m-2.5 p-2.5 text-foreground"
                onClick={onClose}
              >
                <span className="sr-only">Close sidebar</span>
                <X className="h-6 w-6" />
              </Button>
            </div>

            {sidebarContent}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
